import {
  Box,
  Flex,
  Text,
  Button,
  VStack,
  Grid,
  Image,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useDisclosure,
  Link,
  HStack,
} from "@chakra-ui/react";
import { ChevronDownIcon, HamburgerIcon } from "@chakra-ui/icons";
import { useEffect, useMemo, useState, useRef } from "react";
import CartIcon from "../../components/cart-icon/CartIcon";
import { socialMenu } from "../../helper/HeaderMenu";
import { useCartContext } from "../../hooks/cart-context/CartContext";
import ShoppingCart from "../shopping-cart/ShoppingCart";

interface ChildItem {
  title: string;
  image: string;
  url: string;
}

interface ParentMenu {
  url: string;
  children: Record<string, ChildItem[]>;
  dbTable?: string;
}

type MenuData = Record<string, ParentMenu>;

export default function HeaderMenu() {
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [menuData, setMenu] = useState<MenuData>({});
  const [visible, setVisible] = useState(false);
  const { cartState }: any = useCartContext();
  const itemCount: number = useMemo(() => cartState.products.length, [cartState]);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const switchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/productsSideNavs`)
      .then((response) => response.json())
      .then((data) => {
        const formatted: MenuData = {};
        data.forEach((category: any) => {
          const grouped: Record<string, ChildItem[]> = {};
          let parent_url = "";
          if (category.dbTable === "categories" || category.dbTable === "tags") {
            parent_url = `/products/${category.label}`;
          } else if (category.dbTable !== "custom") {
            parent_url = `/products/all-products?${category.dbTable}=${category.label}`;
          }
          category.prds?.forEach((prd: any) => {
            const mapping: Record<string, string> = {
              colors: "Colours",
              finishes: "Finishes",
              sizes: "Sizes",
              categories: "Types",
              tags: "Tags",
            };
            const groupName =
              mapping[prd.dbTable] ||
              prd.dbTable.charAt(0).toUpperCase() + prd.dbTable.slice(1);
            if (!grouped[groupName]) grouped[groupName] = [];
            let url = parent_url;
            if (prd.dbTable === "custom") {
              url = prd.route;
            // } else if (prd.dbTable === "categories" || prd.dbTable === "tags") {
            //   url = `${prd.route}?${category.dbTable}=${encodeURIComponent(category.label)}`;
            } else {
              url += "?" + prd.dbTable + "=" + encodeURIComponent(prd.label);
            }
            grouped[groupName].push({
              title: prd.label,
              image: prd.image,
              url,
            });
          });
          formatted[category.label] = {
            url: parent_url,
            children: grouped,
            dbTable: category.dbTable,
          };
        });
        setMenu(formatted);
      });
  }, []);

  useEffect(() => {
    return () => {
      if (switchTimerRef.current) clearTimeout(switchTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const handleMenuItemEnter = (menu: string, hasChildren: boolean) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (!hasChildren) return;
    if (!hoveredMenu) {
      setHoveredMenu(menu);
      setActiveTab(Object.keys(menuData[menu].children)[0]);
    } else if (hoveredMenu !== menu) {
      if (switchTimerRef.current) clearTimeout(switchTimerRef.current);
      switchTimerRef.current = setTimeout(() => {
        setHoveredMenu(menu);
        setActiveTab(Object.keys(menuData[menu].children)[0]);
        switchTimerRef.current = null;
      }, 120);
    }
  };

  const handleMenuItemLeave = () => {
    if (switchTimerRef.current) {
      clearTimeout(switchTimerRef.current);
      switchTimerRef.current = null;
    }
  };

  const handleNavMouseLeave = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setHoveredMenu(null);
      setActiveTab(null);
      closeTimerRef.current = null;
    }, 180);
  };

  const handleNavMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  return (
    <>
      <Box as="header" bg="#eef4fa" shadow="sm" position="sticky" top={0} zIndex={1000}>
        <Flex
          maxW="7xl"
          mx="auto"
          px={6}
          py={3}
          justify="space-between"
          align="center"
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <IconButton
            aria-label="Open Menu"
            icon={<HamburgerIcon />}
            display={{ base: "flex", md: "none" }}
            onClick={onOpen}
          />
          <Flex flex="1" justify={{ base: "center", md: "flex-start" }}>
            <a href="/" className="header-logo">
              <img src="/lorenza-logo-transparent-blue.png" alt="Logo" />
            </a>
          </Flex>
          <Flex gap={2} align="center" display={{ base: "flex", md: "none" }}>
            <CartIcon visible={visible} setVisible={setVisible} count={itemCount} />
          </Flex>
          <Flex gap={2} align="center" display={{ base: "none", md: "flex" }}>
            {socialMenu?.map((item: any, idx: number) => (
              <Box key={idx}>
                {item.ariaLabel === "ShoppingCart" ? (
                  <CartIcon visible={visible} setVisible={setVisible} count={itemCount} />
                ) : (
                  <IconButton
                    as="a"
                    href={item.href}
                    aria-label={item.ariaLabel}
                    icon={item.icon}
                    size={item.size}
                    variant="ghost"
                    fontSize={item.fontSize}
                    target="_blank"
                  />
                )}
              </Box>
            ))}
          </Flex>
        </Flex>

        <Box
          onMouseEnter={handleNavMouseEnter}
          onMouseLeave={handleNavMouseLeave}
          display={{ base: "none", md: "block" }}
        >
          <Flex justify="center" gap={6} py={3} flexWrap="wrap" rowGap={3} maxW="90%" mx="auto">
            {Object.keys(menuData).map((menu) => {
              const hasChildren = Object.keys(menuData[menu].children).length > 0;
              return (
                <Box
                  key={menu}
                  onMouseEnter={() => handleMenuItemEnter(menu, hasChildren)}
                  onMouseLeave={handleMenuItemLeave}
                  onClick={() => {
                    if (!hasChildren) window.location.href = menuData[menu].url;
                  }}
                >
                  <Button
                    variant="ghost"
                    rightIcon={hasChildren ? <ChevronDownIcon /> : undefined}
                    bg={hoveredMenu === menu ? "#33557b" : "transparent"}
                    color={hoveredMenu === menu ? "white" : "gray.600"}
                    _hover={{ bg: "#33557b", color: "white" }}
                    fontSize="sm"
                    px={4}
                  >
                    {menu}
                  </Button>
                </Box>
              );
            })}
          </Flex>

          {hoveredMenu && activeTab && (
            <Box
              w="100%"
              bg="white"
              borderTop="1px solid"
              borderColor="gray.200"
              shadow="md"
              position="absolute"
              left={0}
              onMouseEnter={handleNavMouseEnter}
              onMouseLeave={handleNavMouseLeave}
            >
              {menuData[hoveredMenu]?.dbTable === "custom" ? (
                <Grid templateColumns="repeat(3, 1fr)" gap={6} flex="1" p={6}>
                  {Object.values(menuData[hoveredMenu].children)
                    .flat()
                    .map((item, idx) => (
                      <Link key={idx} href={item.url} _hover={{ textDecoration: "none" }}>
                        <Box borderRadius="md" overflow="hidden" _hover={{ shadow: "md" }}>
                          <Box position="relative" w="100%" pb="100%">
                            <Image
                              src={process.env.REACT_APP_API_URL+'/optimized-image'+item.image}
                              alt={item.title}
                              position="absolute"
                              top={0}
                              left={0}
                              w="100%"
                              h="100%"
                              objectFit="cover"
                            />
                          </Box>
                          <Text textAlign="center" fontSize="sm" p={2}>
                            {item.title}
                          </Text>
                        </Box>
                      </Link>
                    ))}
                </Grid>
              ) : (
                <Flex>
                  <VStack align="start" borderColor="gray.200" bg="#eef4fa">
                    {Object.keys(menuData[hoveredMenu].children)
                      .sort((a, b) => {
                        const order = ["Colours", "Sizes", "Finishes", "Materials"];
                        const indexA = order.indexOf(a);
                        const indexB = order.indexOf(b);

                        // If both are in order list, sort by index
                        if (indexA !== -1 && indexB !== -1) return indexA - indexB;

                        // If only one is in the list, it comes first
                        if (indexA !== -1) return -1;
                        if (indexB !== -1) return 1;

                        // Otherwise, sort alphabetically for "others"
                        return a.localeCompare(b);
                      })
                      .map((tab) => (
                        <Text
                          key={tab}
                          fontWeight={activeTab === tab ? "bold" : "medium"}
                          bg={activeTab === tab ? "white" : ""}
                          color={activeTab === tab ? "#33557b" : "gray.700"}
                          cursor="pointer"
                          _hover={{ color: "#33557b", bg: "white" }}
                          onMouseEnter={() => setActiveTab(tab)}
                          p="10px"
                          pl="50px"
                          width="200px"
                        >
                          {tab}
                        </Text>
                      ))}
                  </VStack>

                  <Grid templateColumns="repeat(6, 1fr)" gap={6} flex="1" p={6}>
                    {menuData[hoveredMenu].children[activeTab]?.map((item, idx) => (
                      <Link key={idx} href={item.url} _hover={{ textDecoration: "none" }}>
                        <Box borderRadius="md" overflow="hidden" _hover={{ shadow: "md" }}>
                          <Image src={process.env.REACT_APP_API_URL+'/optimized-image'+item.image} alt={item.title} w="100%" h="140px" objectFit="cover" />
                          <Text textAlign="center" fontSize="sm" p={2}>
                            {item.title}
                          </Text>
                        </Box>
                      </Link>
                    ))}
                  </Grid>
                </Flex>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Drawer placement="left" onClose={onClose} isOpen={isOpen} size="xs">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader>
            <a href="/" className="header-logo">
              <img src="/lorenza-logo-transparent-blue.png" alt="Logo" width="50%" />
            </a>
          </DrawerHeader>
          <DrawerCloseButton />
          <DrawerBody p={0}>
            <Accordion allowToggle>
              {Object.keys(menuData).map((menu) => {
                const menuInfo = menuData[menu];
                const hasChildren = Object.keys(menuInfo.children).length > 0;

                // 🔹 Direct link menu (no children)
                if (!hasChildren) {
                  return (
                    <Box key={menu} borderBottom="1px solid" borderColor="gray.100">
                      <Link
                        href={menuInfo.url}
                        _hover={{ textDecoration: "none", bg: "#f7fafc" }}
                        display="block"
                        px={4}
                        py={3}
                      >
                        <Text fontWeight="bold" color="gray.700">
                          {menu}
                        </Text>
                      </Link>
                    </Box>
                  );
                }

                // 🔹 Menu with children (collapsible)
                return (
                  <AccordionItem key={menu} border="none">
                    <h2>
                      <AccordionButton _expanded={{ bg: "#33557b", color: "white" }}>
                        <Box flex="1" textAlign="left" fontWeight="bold">
                          {menu}
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>

                    <AccordionPanel pb={2} px={0}>
                      {/* Nested Accordion for grouped subcategories */}
                      <Accordion allowToggle>
                        {Object.keys(menuInfo.children)
                          .sort((a, b) => {
                            const order = ["Colours", "Sizes", "Finishes", "Materials"];
                            const indexA = order.indexOf(a);
                            const indexB = order.indexOf(b);
                            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                            if (indexA !== -1) return -1;
                            if (indexB !== -1) return 1;
                            return a.localeCompare(b);
                          })
                          .map((category) => {
                            const items = menuInfo.children[category];

                            // ✅ If subcategory is "custom", skip Accordion and list directly
                            if (category.toLowerCase() === "custom") {
                              return (
                                <Box key={category} px={4} py={2}>
                                  {/* <Text fontWeight="semibold" color="#33557b" mb={2}>
                                    {category}
                                  </Text> */}
                                  <Grid templateColumns="repeat(2, 1fr)" gap={4} justifyItems="center">
                                    {items.map((item, idx) => (
                                      <Link
                                        key={idx}
                                        href={item.url}
                                        _hover={{ textDecoration: "none" }}
                                        width="100%"
                                      >
                                        <Box
                                          borderRadius="md"
                                          overflow="hidden"
                                          border="1px solid"
                                          borderColor="gray.100"
                                          bg="white"
                                        >
                                          <Image
                                            src={process.env.REACT_APP_API_URL+'/optimized-image'+item.image}
                                            alt={item.title}
                                            w="100%"
                                            h="100px"
                                            objectFit="cover"
                                          />
                                          <Text textAlign="center" fontSize="sm" py={2} px={1} noOfLines={1}>
                                            {item.title}
                                          </Text>
                                        </Box>
                                      </Link>
                                    ))}
                                  </Grid>
                                </Box>
                              );
                            }

                            // 🔹 Normal categories (keep accordion)
                            return (
                              <AccordionItem key={category} border="none">
                                <h3>
                                  <AccordionButton _expanded={{ bg: "gray.100" }} px={4} py={2}>
                                    <Box flex="1" textAlign="left" fontWeight="semibold" color="#33557b">
                                      {category}
                                    </Box>
                                    <AccordionIcon />
                                  </AccordionButton>
                                </h3>

                                <AccordionPanel pb={4}>
                                  <Grid templateColumns="repeat(2, 1fr)" gap={4} justifyItems="center">
                                    {items.map((item, idx) => (
                                      <Link
                                        key={idx}
                                        href={item.url}
                                        _hover={{ textDecoration: "none" }}
                                        width="100%"
                                      >
                                        <Box
                                          borderRadius="md"
                                          overflow="hidden"
                                          border="1px solid"
                                          borderColor="gray.100"
                                          bg="white"
                                        >
                                          <Image
                                            src={process.env.REACT_APP_API_URL+'/optimized-image'+item.image}
                                            alt={item.title}
                                            w="100%"
                                            h="100px"
                                            objectFit="cover"
                                          />
                                          <Text textAlign="center" fontSize="sm" py={2} px={1} noOfLines={1}>
                                            {item.title}
                                          </Text>
                                        </Box>
                                      </Link>
                                    ))}
                                  </Grid>
                                </AccordionPanel>
                              </AccordionItem>
                            );
                          })}
                      </Accordion>
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </DrawerBody>
        </DrawerContent>
      </Drawer>



      <ShoppingCart visible={visible} setVisible={setVisible} />
    </>
  );
}
